import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
    AccessPayload,
    BatchAccessPayload,
    BatchRevokePayload,
    ServiceAccessPermission,
    ServiceAccessResponse,
    ServiceGroupPayload,
    ServiceItem,
    ServiceListResponse,
    ServicePayload,
    SubscriberListResponse,
    SubscriberGrowthResponse,
    SubscriberSummaryResponse
} from 'src/app/modules/status-page/shared/models/service.models';
import { environment } from 'src/environments/environment';
import {
    CreateIncidentPayload,
    IncidentUpdatePayload,
    EditIncidentUpdatePayload,
    CreateMaintenancePayload,
    UpdateMaintenancePayload,
    CompleteMaintenancePayload,
    CreateIncidentTemplatePayload,
    EditIncidentTemplatePayload,
    MaintenanceTemplatePayload
} from 'src/app/modules/status-page/shared/models/status-page-api.models';
import { PostmortemPayload, PostmortemResponse } from 'src/app/modules/status-page/shared/models/postmortem.model';
import {
    ManageSubscriptionResponse,
    UpdateSubscriberServicesRequest
} from 'src/app/modules/status-page/status-main-page/manage-subscription/status-main-page-manage-subscription.models';

type AccessEntityType = 'service' | 'group' | 'statusPage';
type PublicIncidentQueryOptions = {
    startDate?: string | null;
    endDate?: string | null;
};

// Keep these dynamic because app-config is loaded during bootstrap.
const API_V3_URL = {
    toString: () => String(environment.apiV3Url)
};
const API_BASE_URL = {
    toString: () => String(environment.apiUrl)
};
@Injectable({
    providedIn: 'root'
})
export class StatusPageService {

    constructor(
        private readonly http: HttpClient
    ) { }



    private get apiAccountBaseUrl(): string {
        return `${API_BASE_URL}/account`;
    }

    private getHeaders(token?: string): HttpHeaders {
        const resolvedToken = token || localStorage.getItem('token') || '';
        const headers: Record<string, string> = {
            'Authorization': `Bearer ${resolvedToken}`,
            'Content-Type': 'application/json'
        };
        return new HttpHeaders(headers);
    }

    private getUploadHeaders(token?: string): HttpHeaders {
        const resolvedToken = token || localStorage.getItem('token') || '';
        return new HttpHeaders({
            'Authorization': `Bearer ${resolvedToken}`
        });
    }

    private getPublicSsoHeaders(ssoToken?: string): HttpHeaders {
        const headers: Record<string, string> = {
            'Accept': 'application/json'
        };
        if (ssoToken) {
            headers['Authorization'] = `Bearer ${ssoToken}`;
        }
        return new HttpHeaders(headers);
    }

    private getPublicHeadersWithOptionalSsoToken(ssoToken?: string): HttpHeaders {
        return this.getPublicSsoHeaders(ssoToken);
    }

    private getEntityAccessSegment(entityType: 'service' | 'group'): 'services' | 'service_groups' {
        if (entityType === 'group') {
            return 'service_groups';
        }
        return 'services';
    }

    private buildAccessBaseUrl(id: string | number, entityType: AccessEntityType): string {
        if (entityType === 'statusPage') {
            return `${API_V3_URL}/status_pages/${id}`;
        }
        const entitySegment = this.getEntityAccessSegment(entityType);
        return `${API_V3_URL}/status_pages/${entitySegment}/${id}`;
    }

    private buildAccessUrl(
        id: string | number,
        entityType: AccessEntityType,
        suffix?: string
    ): string {
        const accessUrl = `${this.buildAccessBaseUrl(id, entityType)}/access`;
        return suffix ? `${accessUrl}/${suffix}` : accessUrl;
    }

    getAccountSettings(token: string): Observable<any> {
        return this.http.get<any>(`${this.apiAccountBaseUrl}`, {
            headers: this.getHeaders(token)
        });
    }

    // === Dashboard & System Operations ===

    getSystemOperationsSummary(token: string): Observable<any> {
        return this.http.get<any>(`${API_V3_URL}/system_operations`, {
            headers: this.getHeaders(token)
        });
    }

    // === V3 Dashboard APIs ===

    getDashboardStats(token: string): Observable<any> {
        return this.http.get<any>(`${API_V3_URL}/status_pages/dashboard`, {
            headers: this.getHeaders(token)
        });
    }

    getActiveIncidents(token: string): Observable<any[]> {
        return this.http.get<any[]>(`${API_V3_URL}/status_pages/incidents/active`, {
            headers: this.getHeaders(token)
        });
    }

    createIncidentV3(token: string, payload: CreateIncidentPayload): Observable<any> {
        return this.http.post<any>(`${API_V3_URL}/status_pages/incidents`, payload, {
            headers: this.getHeaders(token)
        });
    }

    createIncidentUpdateV3(token: string, incidentId: string | number, payload: IncidentUpdatePayload): Observable<any> {
        return this.http.post<any>(`${API_V3_URL}/status_pages/incidents/${incidentId}/updates`, payload, {
            headers: this.getHeaders(token)
        });
    }

    updateIncidentUpdateV3(token: string, incidentId: string | number, updateId: string | number, payload: EditIncidentUpdatePayload): Observable<any> {
        return this.http.put<any>(`${API_V3_URL}/status_pages/incidents/${incidentId}/updates/${updateId}`, payload, {
            headers: this.getHeaders(token)
        });
    }

    getIncidentUpdatesV3(token: string, incidentId: string | number): Observable<any[]> {
        return this.http.get<any[]>(`${API_V3_URL}/status_pages/incidents/${incidentId}/updates`, {
            headers: this.getHeaders(token)
        });
    }

    updateIncidentV3(token: string, incidentId: string | number, payload: any): Observable<any> {
        return this.http.put<any>(`${API_V3_URL}/status_pages/incidents/${incidentId}`, payload, {
            headers: this.getHeaders(token)
        });
    }

    deleteIncidentV3(token: string, incidentId: string | number): Observable<any> {
        return this.http.delete<any>(`${API_V3_URL}/status_pages/incidents/${incidentId}`, {
            headers: this.getHeaders(token)
        });
    }

    getUpcomingMaintenance(token: string, daysAhead: number = 30): Observable<any> {
        const params = new HttpParams().append('daysAhead', daysAhead.toString());
        return this.http.get<any>(`${API_V3_URL}/status_pages/maintenance_windows/upcoming`, {
            headers: this.getHeaders(token),
            params
        });
    }

    scheduleMaintenanceV3(token: string, payload: CreateMaintenancePayload): Observable<any> {
        return this.http.post<any>(`${API_V3_URL}/status_pages/maintenance_windows`, payload, {
            headers: this.getHeaders(token)
        });
    }

    updateMaintenanceV3(token: string, id: string | number, payload: UpdateMaintenancePayload): Observable<any> {
        return this.http.put<any>(`${API_V3_URL}/status_pages/maintenance_windows/${id}`, payload, {
            headers: this.getHeaders(token)
        });
    }

    getIncidentHistoryV3(
        token: string,
        includeResolved: boolean = true,
        pageSize: number = 20,
        after?: string,
        startDate?: string | null,
        endDate?: string | null
    ): Observable<any> {
        let params = new HttpParams();
        if (includeResolved) {
            params = params.set('includeResolved', 'true');
        }
        params = params.set('includeActive', 'false');
        params = params.set('pageSize', pageSize.toString());
        if (after) {
            params = params.set('after', after);
        }
        if (startDate) {
            params = params.set('startDate', startDate);
        }
        if (endDate) {
            params = params.set('endDate', endDate);
        }
        return this.http.get<any>(`${API_V3_URL}/status_pages/incidents`, {
            headers: this.getHeaders(token),
            params
        });
    }

    getMaintenanceHistoryV3(
        token: string,
        includePast: boolean = true,
        pageSize: number = 20,
        after?: string,
        startDate?: string | null,
        endDate?: string | null
    ): Observable<any> {
        let params = new HttpParams();
        if (includePast) {
            params = params.set('includePast', 'true');
        }
        params = params.set('includeUpcoming', 'false');
        params = params.set('pageSize', pageSize.toString());
        if (after) {
            params = params.set('after', after);
        }
        if (startDate) {
            params = params.set('startDate', startDate);
        }
        if (endDate) {
            params = params.set('endDate', endDate);
        }
        return this.http.get<any>(`${API_V3_URL}/status_pages/maintenance_windows`, {
            headers: this.getHeaders(token),
            params
        });
    }

    createMaintenanceUpdateV3(token: string, maintenanceId: string | number, payload: any): Observable<any> {
        return this.http.post<any>(`${API_V3_URL}/status_pages/maintenance_windows/${maintenanceId}/updates`, payload, {
            headers: this.getHeaders(token)
        });
    }

    updateMaintenanceUpdateV3(token: string, maintenanceId: string | number, updateId: string | number, payload: any): Observable<any> {
        return this.http.put<any>(`${API_V3_URL}/status_pages/maintenance_windows/${maintenanceId}/updates/${updateId}`, payload, {
            headers: this.getHeaders(token)
        });
    }

    cancelMaintenanceV3(token: string, maintenanceId: string | number): Observable<any> {
        return this.http.post<any>(`${API_V3_URL}/status_pages/maintenance_windows/${maintenanceId}/cancel`, null, {
            headers: this.getHeaders(token)
        });
    }

    deleteMaintenanceV3(token: string, maintenanceId: string | number): Observable<any> {
        return this.cancelMaintenanceV3(token, maintenanceId);
    }

    completeMaintenanceV3(token: string, id: string | number, payload: CompleteMaintenancePayload): Observable<any> {
        return this.http.post<any>(`${API_V3_URL}/status_pages/maintenance_windows/${id}/complete`, payload, {
            headers: this.getHeaders(token)
        });
    }

    // === Incidents ===

    getIncidents(token: string, status?: string): Observable<any> {
        let params = new HttpParams();
        if (status) params = params.append('status', status);
        return this.http.get<any>(`${API_V3_URL}/incidents`, {
            headers: this.getHeaders(token),
            params: params
        });
    }

    createIncident(token: string, payload: any): Observable<any> {
        return this.http.post<any>(`${API_V3_URL}/incidents`, payload, {
            headers: this.getHeaders(token)
        });
    }

    // === Maintenance Schedules ===

    getMaintenances(token: string, scheduled: boolean = true): Observable<any> {
        const params = new HttpParams().append('scheduled', scheduled.toString());
        return this.http.get<any>(`${API_V3_URL}/maintenance_schedules`, {
            headers: this.getHeaders(token),
            params: params
        });
    }

    scheduleMaintenance(token: string, payload: any): Observable<any> {
        return this.http.post<any>(`${API_V3_URL}/maintenance_schedules`, payload, {
            headers: this.getHeaders(token)
        });
    }

    updateMaintenanceUpdate(token: string, id: string | number, payload: any): Observable<any> {
        return this.http.put<any>(`${API_V3_URL}/maintenance_schedules/${id}/maintenance_update`, payload, {
            headers: this.getHeaders(token)
        });
    }

    updateMaintenanceBasic(token: string, id: string | number, payload: any): Observable<any> {
        return this.http.put<any>(`${API_V3_URL}/maintenance_schedules/${id}/basic`, payload, {
            headers: this.getHeaders(token)
        });
    }

    completeMaintenance(token: string, id: string | number): Observable<any> {
        return this.http.put<any>(`${API_V3_URL}/maintenance_schedules/${id}/status`, { status: 'completed' }, {
            headers: this.getHeaders(token)
        });
    }

    /// === Postmortems ===
    getIncidentPostmortem(token: string, incidentId: number | string): Observable<PostmortemResponse> {
        return this.http.get<PostmortemResponse>(`${API_V3_URL}/status_pages/incidents/${incidentId}/postmortem`, {
            headers: this.getHeaders(token)
        });
    }

    getInternalIncidentPostmortemBySlug(
        slug: string,
        incidentId: number | string,
        token?: string
    ): Observable<PostmortemResponse> {
        return this.http.get<PostmortemResponse>(
            `${API_V3_URL}/status_pages/internal/${slug}/incidents/${incidentId}/postmortem`,
            { headers: this.getHeaders(token) }
        );
    }

    getPublicIncidentPostmortemByOrgSlug(
        org: string,
        slug: string,
        incidentId: number | string,
        ssoToken?: string
    ): Observable<PostmortemResponse> {
        return this.http.get<PostmortemResponse>(
            `${API_V3_URL}/public/status_pages/${org}/${slug}/incidents/${incidentId}/postmortem`,
            { headers: this.getPublicHeadersWithOptionalSsoToken(ssoToken) }
        );
    }

    createIncidentPostmortem(token: string, incidentId: number | string, payload: PostmortemPayload): Observable<PostmortemResponse> {
        return this.http.post<PostmortemResponse>(`${API_V3_URL}/status_pages/incidents/${incidentId}/postmortem`, payload, {
            headers: this.getHeaders(token)
        });
    }

    updateIncidentPostmortem(token: string, incidentId: number | string, payload: PostmortemPayload): Observable<PostmortemResponse> {
        return this.http.put<PostmortemResponse>(`${API_V3_URL}/status_pages/incidents/${incidentId}/postmortem`, payload, {
            headers: this.getHeaders(token)
        });
    }

    deleteIncidentPostmortem(token: string, incidentId: number | string): Observable<void> {
        return this.http.delete<void>(`${API_V3_URL}/status_pages/incidents/${incidentId}/postmortem`, {
            headers: this.getHeaders(token)
        });
    }

    publishIncidentPostmortem(token: string, incidentId: number | string): Observable<PostmortemResponse> {
        return this.http.post<PostmortemResponse>(`${API_V3_URL}/status_pages/incidents/${incidentId}/postmortem/publish`, {}, {
            headers: this.getHeaders(token)
        });
    }

    unpublishIncidentPostmortem(token: string, incidentId: number | string): Observable<PostmortemResponse> {
        return this.http.post<PostmortemResponse>(`${API_V3_URL}/status_pages/incidents/${incidentId}/postmortem/unpublish`, {}, {
            headers: this.getHeaders(token)
        });
    }

    // === Status Pages Admin ===

    getStatusPages(token: string): Observable<any[]> {
        return this.http.get<any[]>(`${API_V3_URL}/status_pages`, {
            headers: this.getHeaders(token)
        });
    }

    getStatusPageConfig(token: string): Observable<{ subdomainAliasSuffix?: string }> {
        return this.http.get<{ subdomainAliasSuffix?: string }>(`${API_V3_URL}/status_pages/config`, {
            headers: this.getHeaders(token)
        });
    }

    createStatusPage(token: string, payload: any): Observable<any> {
        return this.http.post<any>(`${API_V3_URL}/status_pages`, payload, {
            headers: this.getHeaders(token)
        });
    }

    getStatusPageDetails(token: string, id: string | number): Observable<any> {
        return this.http.get<any>(`${API_V3_URL}/status_pages/${id}`, {
            headers: this.getHeaders(token)
        });
    }

    updateStatusPage(token: string, id: string | number, payload: any): Observable<any> {
        return this.http.put<any>(`${API_V3_URL}/status_pages/${id}`, payload, {
            headers: this.getHeaders(token)
        });
    }

    deleteStatusPage(token: string, id: string | number): Observable<any> {
        return this.http.delete<any>(`${API_V3_URL}/status_pages/${id}`, {
            headers: this.getHeaders(token)
        });
    }

    getStatusPageSsoConfig(token: string, statusPageId: string | number): Observable<any> {
        return this.http.get<any>(`${API_V3_URL}/status_pages/${statusPageId}/sso`, {
            headers: this.getHeaders(token)
        });
    }

    updateStatusPageSsoConfig(token: string, statusPageId: string | number, payload: any): Observable<any> {
        return this.http.put<any>(`${API_V3_URL}/status_pages/${statusPageId}/sso`, payload, {
            headers: this.getHeaders(token)
        });
    }

    deleteStatusPageSsoConfig(token: string, statusPageId: string | number): Observable<any> {
        return this.http.delete<any>(`${API_V3_URL}/status_pages/${statusPageId}/sso`, {
            headers: this.getHeaders(token)
        });
    }

    createStatusPageCustomDomain(token: string, statusPageId: string | number, payload: any): Observable<any> {
        return this.http.post<any>(`${API_V3_URL}/status_pages/${statusPageId}/custom_domain`, payload, {
            headers: this.getHeaders(token)
        });
    }

    getStatusPageCustomDomain(token: string, statusPageId: string | number): Observable<any> {
        return this.http.get<any>(`${API_V3_URL}/status_pages/${statusPageId}/custom_domain`, {
            headers: this.getHeaders(token)
        });
    }

    deleteStatusPageCustomDomain(
        token: string,
        statusPageId: string | number,
        verificationId: string | number
    ): Observable<any> {
        return this.http.delete<any>(`${API_V3_URL}/status_pages/${statusPageId}/custom_domain/${verificationId}`, {
            headers: this.getHeaders(token)
        });
    }

    getStatusPageSsl(token: string, statusPageId: string | number): Observable<any> {
        return this.http.get<any>(`${API_V3_URL}/status_pages/${statusPageId}/ssl`, {
            headers: this.getHeaders(token)
        });
    }

    warmupStatusPageSsl(token: string, statusPageId: string | number): Observable<any> {
        return this.http.post<any>(`${API_V3_URL}/status_pages/${statusPageId}/ssl/warmup`, {}, {
            headers: this.getHeaders(token)
        });
    }

    refreshStatusPageSsl(token: string, statusPageId: string | number): Observable<any> {
        return this.http.post<any>(`${API_V3_URL}/status_pages/${statusPageId}/ssl/refresh`, {}, {
            headers: this.getHeaders(token)
        });
    }

    getStatusPageCustomDomainVerification(
        token: string,
        statusPageId: string | number,
        verificationId: string | number
    ): Observable<any> {
        return this.http.get<any>(`${API_V3_URL}/status_pages/${statusPageId}/custom_domain/${verificationId}`, {
            headers: this.getHeaders(token)
        });
    }

    checkStatusPageCustomDomainVerification(
        token: string,
        statusPageId: string | number,
        verificationId: string | number
    ): Observable<any> {
        return this.http.post<any>(
            `${API_V3_URL}/status_pages/${statusPageId}/custom_domain/${verificationId}/check`,
            {},
            {
                headers: this.getHeaders(token)
            }
        );
    }

    publishStatusPage(token: string, statusPageId: string | number): Observable<any> {
        return this.http.post<any>(`${API_V3_URL}/status_pages/${statusPageId}/publish`, {}, {
            headers: this.getHeaders(token)
        });
    }

    unpublishStatusPage(token: string, statusPageId: string | number): Observable<any> {
        return this.http.post<any>(`${API_V3_URL}/status_pages/${statusPageId}/unpublish`, {}, {
            headers: this.getHeaders(token)
        });
    }

    getSubscriberStats(token: string, id: string | number): Observable<any> {
        return this.http.get<any>(`${API_V3_URL}/status_pages/${id}/subscribers_stat`, {
            headers: this.getHeaders(token)
        });
    }

    getNotificationStats(token: string, id: string | number): Observable<any> {
        return this.http.get<any>(`${API_V3_URL}/status_pages/${id}/notifications_stat`, {
            headers: this.getHeaders(token)
        });
    }

    getAccessDetails(token: string, id: string | number): Observable<any> {
        return this.http.get<any>(`${API_V3_URL}/status_pages/${id}/access`, {
            headers: this.getHeaders(token)
        });
    }

    updateAccessStatus(token: string, id: string | number, enableCustomAccess: boolean): Observable<any> {
        return this.http.put<any>(`${API_V3_URL}/status_pages/${id}/access/status`, { enable_custom_access: enableCustomAccess.toString() }, {
            headers: this.getHeaders(token)
        });
    }

    updateOwner(token: string, id: string | number, userId: string | number): Observable<any> {
        return this.http.put<any>(`${API_V3_URL}/status_pages/${id}/access/owner`, { user_id: userId }, {
            headers: this.getHeaders(token)
        });
    }

    addAccess(token: string, id: string | number, payload: any): Observable<any> {
        return this.http.post<any>(`${API_V3_URL}/status_pages/${id}/access/user`, payload, {
            headers: this.getHeaders(token)
        });
    }

    updateAccessLevel(token: string, id: string | number, entityId: string | number, userType: string, accessLevel: string): Observable<any> {
        return this.http.put<any>(`${API_V3_URL}/status_pages/${id}/access/user/${entityId}/${userType}`, { access_level: accessLevel }, {
            headers: this.getHeaders(token)
        });
    }

    deleteAccess(token: string, id: string | number, entityId: string | number): Observable<any> {
        return this.http.delete<any>(`${API_V3_URL}/status_pages/${id}/access/user/${entityId}`, {
            headers: this.getHeaders(token)
        });
    }

    getSubscribers(
        token: string,
        id: string | number,
        options?: {
            pageSize?: number;
            after?: string;
            before?: string;
            status?: string;
            includeCount?: boolean;
        }
    ): Observable<SubscriberListResponse> {
        let params = new HttpParams();
        if (options?.pageSize !== undefined) params = params.set('pageSize', String(options.pageSize));
        if (options?.after) params = params.set('after', options.after);
        if (options?.before) params = params.set('before', options.before);
        if (options?.status) params = params.set('status', options.status);
        if (options?.includeCount !== undefined) params = params.set('includeCount', String(options.includeCount));
        return this.http.get<SubscriberListResponse>(`${API_V3_URL}/status_pages/${id}/subscribers`, {
            headers: this.getHeaders(token),
            params
        });
    }

    getSubscriberSummary(token: string, id: string | number): Observable<SubscriberSummaryResponse> {
        return this.http.get<SubscriberSummaryResponse>(`${API_V3_URL}/status_pages/${id}/subscribers/summary`, {
            headers: this.getHeaders(token)
        });
    }

    getSubscriberGrowth(token: string, id: string | number): Observable<SubscriberGrowthResponse> {
        return this.http.get<SubscriberGrowthResponse>(`${API_V3_URL}/status_pages/${id}/subscribers/growth`, {
            headers: this.getHeaders(token)
        });
    }

    exportSubscribers(token: string, id: string | number, viewBy: string = 'A'): Observable<any> {
        const params = new HttpParams().append('viewBy', viewBy);
        return this.http.get<any>(`${API_V3_URL}/status_pages/${id}/subscribers/export`, {
            headers: this.getHeaders(token),
            params: params
        });
    }

    deleteStatusPageSubscriber(
        token: string,
        statusPageId: string | number,
        subscriberId: string | number
    ): Observable<any> {
        return this.http.delete<any>(`${API_V3_URL}/status_pages/${statusPageId}/subscribers/${subscriberId}`, {
            headers: this.getHeaders(token)
        });
    }

    deleteSubscriber(token: string, id: string | number, subscriberId: string | number): Observable<any> {
        return this.deleteStatusPageSubscriber(token, id, subscriberId);
    }

    getStatusPageNotificationUsage(
        token: string,
        options: {
            statusPageId?: string | number | null;
            fromDate?: string;
            toDate?: string;
        } = {}
    ): Observable<any> {
        let params = new HttpParams();
        const { statusPageId, fromDate, toDate } = options;
        if (statusPageId !== undefined && statusPageId !== null && String(statusPageId).trim() !== '') {
            params = params.set('statusPageId', String(statusPageId));
        }
        if (fromDate) params = params.set('fromDate', fromDate);
        if (toDate) params = params.set('toDate', toDate);
        return this.http.get<any>(`${API_V3_URL}/status_pages/notifications/usage`, {
            headers: this.getHeaders(token),
            params
        });
    }

    getStatusPageNotificationUsageLog(
        token: string,
        statusPageId: string | number | null | undefined,
        fromDate: string,
        toDate: string,
        options: {
            includeStatusPages?: boolean;
        } = {}
    ): Observable<any> {
        let params = new HttpParams();
        if (statusPageId !== undefined && statusPageId !== null && String(statusPageId).trim() !== '') {
            params = params.set('statusPageId', String(statusPageId));
        }
        if (fromDate) params = params.set('fromDate', fromDate);
        if (toDate) params = params.set('toDate', toDate);
        if (options.includeStatusPages) params = params.set('includeStatusPages', 'true');
        return this.http.get<any>(`${API_V3_URL}/status_pages/notifications/usage/log`, {
            headers: this.getHeaders(token),
            params
        });
    }

    getStatusPageIncidentNotificationsUsage(
        token: string,
        startDate: string,
        endDate: string,
        options: {
            pageSize?: number;
            pageNumber?: number;
        } = {}
    ): Observable<any> {
        let params = new HttpParams();
        if (startDate) params = params.set('startDate', startDate);
        if (endDate) params = params.set('endDate', endDate);
        if (options.pageSize !== undefined) params = params.set('pageSize', String(options.pageSize));
        if (options.pageNumber !== undefined) params = params.set('pageNumber', String(options.pageNumber));
       
        return this.http.get<any>(`${API_V3_URL}/status_pages/notifications/incidents`, {
            headers: this.getHeaders(token),
            params
        });
    }

    getStatusPageMaintenanceNotificationsUsage(
        token: string,
        startDate: string,
        endDate: string,
        options: {
            pageSize?: number;
            pageNumber?: number;
        } = {}
    ): Observable<any> {
        let params = new HttpParams();
        if (startDate) params = params.set('startDate', startDate);
        if (endDate) params = params.set('endDate', endDate);
        if (options.pageSize !== undefined) params = params.set('pageSize', String(options.pageSize));
        if (options.pageNumber !== undefined) params = params.set('pageNumber', String(options.pageNumber));
       
        return this.http.get<any>(`${API_V3_URL}/status_pages/notifications/maintenance_windows`, {
            headers: this.getHeaders(token),
            params
        });
    }

    getPreview(token: string, id: string | number): Observable<any> {
        return this.http.get<any>(`${API_V3_URL}/status_pages/${id}/preview`, {
            headers: this.getHeaders(token)
        });
    }

    createSubscription(token: string, id: string | number, payload: any): Observable<any> {
        return this.http.post<any>(`${API_V3_URL}/status_pages/${id}/subscribers`, payload, {
            headers: this.getHeaders(token)
        });
    }

    getLivePage(token: string, id: string | number): Observable<any> {
        return this.http.get<any>(`${API_V3_URL}/status_pages/${id}/live`, {
            headers: this.getHeaders(token)
        });
    }

    // === Service Tree ===
    getGroupedServices(token: string): Observable<any> {
        return this.http.get<any>(`${API_V3_URL}/status_pages/services/grouped`, {
            headers: this.getHeaders(token)
        });
    }



    // === Status Page Message Templates ===
    getIncidentTemplates(token: string, activeOnly: boolean = true): Observable<any[]> {
        let params = new HttpParams();
        if (activeOnly) {
            params = params.append('activeOnly', 'true');
        }
        return this.http.get<any[]>(`${API_V3_URL}/status_pages/incident_templates`, {
            headers: this.getHeaders(token),
            params: params
        });
    }

    getMaintenanceTemplates(token: string, activeOnly: boolean = true): Observable<any[]> {
        let params = new HttpParams();
        if (activeOnly) {
            params = params.append('activeOnly', 'true');
        }
        return this.http.get<any[]>(`${API_V3_URL}/status_pages/maintenance_templates`, {
            headers: this.getHeaders(token),
            params: params
        });
    }

    createIncidentTemplate(token: string, payload: CreateIncidentTemplatePayload): Observable<any> {
        return this.http.post<any>(`${API_V3_URL}/status_pages/incident_templates`, payload, {
            headers: this.getHeaders(token)
        });
    }

    createMaintenanceTemplate(token: string, payload: MaintenanceTemplatePayload): Observable<any> {
        return this.http.post<any>(`${API_V3_URL}/status_pages/maintenance_templates`, payload, {
            headers: this.getHeaders(token)
        });
    }

    updateIncidentTemplate(token: string, id: string | number, payload: EditIncidentTemplatePayload): Observable<any> {
        return this.http.put<any>(`${API_V3_URL}/status_pages/incident_templates/${id}`, payload, {
            headers: this.getHeaders(token)
        });
    }

    updateMaintenanceTemplate(token: string, id: string | number, payload: MaintenanceTemplatePayload): Observable<any> {
        return this.http.put<any>(`${API_V3_URL}/status_pages/maintenance_templates/${id}`, payload, {
            headers: this.getHeaders(token)
        });
    }

    deleteTemplate(token: string, id: string | number, type: 'incident' | 'maintenance'): Observable<any> {
        const path = type === 'incident' ? 'incident_templates' : 'maintenance_templates';
        return this.http.delete<any>(`${API_V3_URL}/status_pages/${path}/${id}`, {
            headers: this.getHeaders(token)
        });
    }

    // === Service Catalog ===

    /**
     * Get services with cursor-based pagination.
     * Token is optional â€” if omitted, the service reads it from localStorage internally.
     * @param token Authorization token (optional)
     * @param pageSize Number of items per page (default: 20)
     * @param prevCursor Cursor for previous page navigation
     * @param nextCursor Cursor for next page navigation
     * @returns Observable of ServiceListResponse with paginated data
     */
    getServices(
        token?: string,
        pageSize: number = 20,
        before?: string | null,
        after?: string | null
    ): Observable<ServiceListResponse> {
        let params = new HttpParams();
        params = params.set('pageSize', pageSize.toString());

        if (before) {
            params = params.set('before', before);
        }
        if (after) {
            params = params.set('after', after);
        }

        return this.http.get<ServiceListResponse>(
            `${API_V3_URL}/status_pages/services`,
            {
                headers: this.getHeaders(token),
                params: params
            }
        );
    }

    // === Service Groups ===

    getServiceGroups(token: string): Observable<any> {
        return this.http.get<any>(`${API_V3_URL}/status_pages/service_groups`, {
            headers: this.getHeaders(token)
        });
    }

    createServiceGroup(token: string, payload: ServiceGroupPayload): Observable<ServiceItem> {
        return this.http.post<ServiceItem>(`${API_V3_URL}/status_pages/service_groups`, payload, {
            headers: this.getHeaders(token)
        });
    }

    updateServiceGroup(token: string, groupId: string | number, payload: ServiceGroupPayload): Observable<ServiceItem> {
        return this.http.put<ServiceItem>(`${API_V3_URL}/status_pages/service_groups/${groupId}`, payload, {
            headers: this.getHeaders(token)
        });
    }

    deleteServiceGroup(token: string, groupId: string | number): Observable<any> {
        return this.http.delete<any>(`${API_V3_URL}/status_pages/service_groups/${groupId}`, {
            headers: this.getHeaders(token)
        });
    }

    getServicesByGroupId(token: string, groupId: string | number): Observable<any[]> {
        return this.http.get<any[]>(`${API_V3_URL}/status_pages/service_groups/${groupId}/services`, {
            headers: this.getHeaders(token)
        });
    }



    createStandaloneService(token: string, payload: ServicePayload): Observable<ServiceItem> {
        return this.http.post<ServiceItem>(`${API_V3_URL}/status_pages/services`, payload, {
            headers: this.getHeaders(token)
        });
    }

    updateService(token: string, serviceId: string | number, payload: Omit<ServicePayload, 'currentStatus'>): Observable<ServiceItem> {
        return this.http.put<ServiceItem>(`${API_V3_URL}/status_pages/services/${serviceId}`, payload, {
            headers: this.getHeaders(token)
        });
    }

    deleteService(token: string, serviceId: string | number): Observable<void> {
        return this.http.delete<void>(`${API_V3_URL}/status_pages/services/${serviceId}`, {
            headers: this.getHeaders(token)
        });
    }

    addServiceAccess(token: string, serviceId: string | number, payload: AccessPayload): Observable<ServiceAccessResponse> {
        return this.http.post<ServiceAccessResponse>(`${API_V3_URL}/status_pages/services/${serviceId}/access`, payload, {
            headers: this.getHeaders(token)
        });
    }

    addServiceAccessBatch(token: string, id: string | number, payload: BatchAccessPayload, entityType: AccessEntityType = 'service'): Observable<any> {
        const url = this.buildAccessUrl(id, entityType);
        return this.http.post<any>(url, payload, {
            headers: this.getHeaders(token)
        });
    }

    updateServiceAccessBatch(token: string, id: string | number, payload: BatchAccessPayload, entityType: AccessEntityType = 'service'): Observable<any> {
        const url = this.buildAccessUrl(id, entityType);
        return this.http.put<any>(url, payload, {
            headers: this.getHeaders(token)
        });
    }

    removeServiceAccessBatch(token: string, id: string | number, payload: BatchRevokePayload, entityType: AccessEntityType = 'service'): Observable<any> {
        const url = this.buildAccessUrl(id, entityType);
        return this.http.delete<any>(url, {
            headers: this.getHeaders(token),
            body: payload
        });
    }

    transferServiceOwnership(token: string, serviceId: string | number, newOwnerUserId: string | number): Observable<any> {
        return this.http.post<any>(`${API_V3_URL}/status_pages/services/${serviceId}/access/transfer_ownership`, { newOwnerUserId }, {
            headers: this.getHeaders(token)
        });
    }

    transferServiceGroupOwnership(token: string, serviceGroupId: string | number, newOwnerUserId: string | number): Observable<any> {
        return this.http.post<any>(`${API_V3_URL}/status_pages/service_groups/${serviceGroupId}/access/transfer_ownership`, { newOwnerUserId }, {
            headers: this.getHeaders(token)
        });
    }

    transferStatusPageOwnership(token: string, statusPageId: string | number, newOwnerUserId: string | number): Observable<any> {
        return this.http.post<any>(`${API_V3_URL}/status_pages/${statusPageId}/access/transfer_ownership`, { newOwnerUserId }, {
            headers: this.getHeaders(token)
        });
    }

    /**
     * Transfer ownership for services, service groups, or status pages based on entity type.
     */
    transferOwnership(
        token: string,
        id: string | number,
        newOwnerUserId: string | number,
        entityType: AccessEntityType = 'service'
    ): Observable<any> {
        if (entityType === 'statusPage') {
            return this.transferStatusPageOwnership(token, id, newOwnerUserId);
        }
        if (entityType === 'group') {
            return this.transferServiceGroupOwnership(token, id, newOwnerUserId);
        }
        return this.transferServiceOwnership(token, id, newOwnerUserId);
    }

    getServiceAccessPermission(token: string, id: string | number, entityType: AccessEntityType = 'service'): Observable<ServiceAccessPermission> {
        const url = this.buildAccessUrl(id, entityType, 'permission');
        return this.http.get<ServiceAccessPermission>(url, {
            headers: this.getHeaders(token)
        });
    }

    restrictServiceAccess(token: string, id: string | number, entityType: AccessEntityType = 'service'): Observable<any> {
        const url = this.buildAccessUrl(id, entityType, 'restrict');
        return this.http.post<any>(url, null, {
            headers: this.getHeaders(token)
        });
    }

    unrestrictServiceAccess(token: string, id: string | number, entityType: AccessEntityType = 'service'): Observable<any> {
        const url = this.buildAccessUrl(id, entityType, 'restrict');
        return this.http.delete<any>(url, {
            headers: this.getHeaders(token)
        });
    }

    getServiceAccess(token: string, id: string | number, entityType: AccessEntityType = 'service'): Observable<ServiceAccessResponse[]> {
        const url = this.buildAccessUrl(id, entityType);
        return this.http.get<ServiceAccessResponse[]>(url, {
            headers: this.getHeaders(token)
        });
    }

    getAvailableUsers(token: string, id: string | number, entityType: AccessEntityType = 'service'): Observable<any[]> {
        const url = this.buildAccessUrl(id, entityType, 'available_users');
        return this.http.get<any[]>(url, {
            headers: this.getHeaders(token)
        });
    }

    getAvailableGroups(token: string, id: string | number, entityType: AccessEntityType = 'service'): Observable<any[]> {
        const url = this.buildAccessUrl(id, entityType, 'available_groups');
        return this.http.get<any[]>(url, {
            headers: this.getHeaders(token)
        });
    }

    addGroupAccess(token: string, groupId: string | number, payload: any): Observable<any> {
        return this.http.post<any>(`${API_V3_URL}/status_pages/service_groups/${groupId}/access`, payload, {
            headers: this.getHeaders(token)
        });
    }

    updateGroupAccess(token: string, groupId: string | number, accessId: string | number, payload: any): Observable<any> {
        return this.http.put<any>(`${API_V3_URL}/status_pages/service_groups/${groupId}/access/${accessId}`, payload, {
            headers: this.getHeaders(token)
        });
    }

    toggleGroupCustomAccess(token: string, groupId: string | number, accessId: string | number, enableCustomAccess: boolean): Observable<any> {
        return this.http.put<any>(`${API_V3_URL}/status_pages/service_groups/${groupId}/access/${accessId}`, { enable_custom_access: enableCustomAccess.toString() }, {
            headers: this.getHeaders(token)
        });
    }

    deleteGroupAccess(token: string, groupId: string | number, accessId: string | number): Observable<any> {
        return this.http.delete<any>(`${API_V3_URL}/status_pages/service_groups/${groupId}/access/${accessId}`, {
            headers: this.getHeaders(token)
        });
    }

    /**
     * Remove a single service from a group
     * @param token Authorization token
     * @param groupId The target group ID
     * @param serviceId The ID of the service to remove
     */
    removeServiceFromGroup(token: string, groupId: string | number, serviceId: string | number): Observable<any> {
        return this.http.delete<any>(`${API_V3_URL}/status_pages/service_groups/${groupId}/services/${serviceId}`, {
            headers: this.getHeaders(token)
        });
    }

    /**
     * Batch-remove services from a group
     * @param token Authorization token
     * @param groupId The target group ID
     * @param payload Object containing service IDs to remove from the group
     */
    removeServicesFromGroupBatch(
        token: string,
        groupId: string | number,
        payload: { serviceIds: number[] }
    ): Observable<any> {
        return this.http.delete<any>(`${API_V3_URL}/status_pages/service_groups/${groupId}/services`, {
            headers: this.getHeaders(token),
            body: payload
        });
    }

    /**
     * Batch-add existing services to a group
     * @param token  Authorization token
     * @param groupId  The target group ID
     * @param payload BatchServiceGroupPayload object containing services to associate
     */
    addServicesToGroupBatch(token: string, groupId: string | number, payload: import('src/app/modules/status-page/shared/models/service.models').BatchServiceGroupPayload): Observable<any> {
        return this.http.post<any>(`${API_V3_URL}/status_pages/service_groups/${groupId}/services`, payload, {
            headers: this.getHeaders(token)
        });
    }

    /**
     * Batch-update service display order in a group
     * @param token Authorization token
     * @param groupId The target group ID
     * @param payload BatchServiceGroupPayload object containing services with displayOrder
     */
    updateGroupServicesOrder(token: string, groupId: string | number, payload: import('src/app/modules/status-page/shared/models/service.models').BatchServiceGroupPayload): Observable<any> {
        return this.http.put<any>(`${API_V3_URL}/status_pages/service_groups/${groupId}/services`, payload, {
            headers: this.getHeaders(token)
        });
    }

    // INTERNAL STATUS PAGE APIs

    getInternalServices(token: string, statusPageId: string | number): Observable<any> {
        return this.http.get<any>(
            `${API_V3_URL}/status_pages/${statusPageId}/services`,
            { headers: this.getHeaders(token) }
        );
    }

    getInternalIncidents(token: string, statusPageId: string | number): Observable<any> {
        return this.http.get<any>(
            `${API_V3_URL}/status_pages/${statusPageId}/incidents`,
            { headers: this.getHeaders(token) }
        );
    }

    getInternalMaintenances(token: string, statusPageId: string | number): Observable<any> {
        return this.http.get<any>(
            `${API_V3_URL}/status_pages/${statusPageId}/maintenances`,
            { headers: this.getHeaders(token) }
        );
    }

    subscribeInternalStatusPage(
        token: string,
        statusPageId: string | number,
        payload: any
    ): Observable<any> {
        return this.http.post<any>(
            `${API_V3_URL}/status_pages/${statusPageId}/subscribers`,
            payload,
            { headers: this.getHeaders(token) }
        );
    }

    unsubscribeInternalStatusPage(
        token: string,
        statusPageId: string | number,
        subscriberId: string | number
    ): Observable<any> {
        return this.deleteStatusPageSubscriber(token, statusPageId, subscriberId);
    }



    // INTERNAL STATUS PAGE APIs (SLUG-BASED)

    getInternalStatusPageBySlug(slug: string, token?: string): Observable<any> {
        return this.http.get<any>(
            `${API_V3_URL}/status_pages/internal/${slug}`,
            { headers: this.getHeaders(token) }
        );
    }

    getInternalServicesBySlug(
        slug: string,
        options?: {
            grouped?: boolean;
            pageSize?: number;
            after?: string;
            before?: string;
            includeCount?: boolean;
        },
        token?: string
    ): Observable<any> {
        let params = new HttpParams();
        if (options?.grouped !== undefined) params = params.set('grouped', String(options.grouped));
        if (options?.pageSize !== undefined) params = params.set('pageSize', String(options.pageSize));
        if (options?.after) params = params.set('after', options.after);
        if (options?.before) params = params.set('before', options.before);
        if (options?.includeCount !== undefined) params = params.set('includeCount', String(options.includeCount));
        return this.http.get<any>(
            `${API_V3_URL}/status_pages/internal/${slug}/services`,
            { params, headers: this.getHeaders(token) }
        );
    }

    getInternalStatusSummaryBySlug(slug: string, token?: string): Observable<any> {
        return this.http.get<any>(
            `${API_V3_URL}/status_pages/internal/${slug}/status/summary`,
            { headers: this.getHeaders(token) }
        );
    }

    getInternalIncidentsBySlug(
        slug: string,
        options?: {
            days?: number;
            pageSize?: number;
            after?: string;
            before?: string;
            startDate?: string | null;
            endDate?: string | null;
        },
        token?: string
    ): Observable<any> {
        let params = new HttpParams();
        if (options?.days !== undefined) params = params.set('days', String(options.days));
        if (options?.pageSize !== undefined) params = params.set('pageSize', String(options.pageSize));
        if (options?.after) params = params.set('after', options.after);
        if (options?.before) params = params.set('before', options.before);
        if (options?.startDate) params = params.set('startDate', options.startDate);
        if (options?.endDate) params = params.set('endDate', options.endDate);
        return this.http.get<any>(
            `${API_V3_URL}/status_pages/internal/${slug}/incidents`,
            { params, headers: this.getHeaders(token) }
        );
    }

    getInternalActiveIncidentsBySlug(slug: string, token?: string): Observable<any> {
        return this.http.get<any>(
            `${API_V3_URL}/status_pages/internal/${slug}/incidents/active`,
            { headers: this.getHeaders(token) }
        );
    }

    getInternalIncidentBySlug(slug: string, incidentId: string | number, token?: string): Observable<any> {
        return this.http.get<any>(
            `${API_V3_URL}/status_pages/internal/${slug}/incidents/${incidentId}`,
            { headers: this.getHeaders(token) }
        );
    }


    getInternalMaintenanceBySlug(
        slug: string,
        options?: {
            days?: number;
            pageSize?: number;
            after?: string;
            before?: string;
        },
        token?: string
    ): Observable<any> {
        let params = new HttpParams();
        if (options?.days !== undefined) params = params.set('days', String(options.days));
        if (options?.pageSize !== undefined) params = params.set('pageSize', String(options.pageSize));
        if (options?.after) params = params.set('after', options.after);
        if (options?.before) params = params.set('before', options.before);
        return this.http.get<any>(
            `${API_V3_URL}/status_pages/internal/${slug}/maintenance`,
            { params, headers: this.getHeaders(token) }
        );
    }

    getInternalUpcomingMaintenanceBySlug(slug: string, daysAhead?: number, token?: string): Observable<any> {
        let params = new HttpParams();
        if (daysAhead !== undefined) params = params.set('daysAhead', String(daysAhead));
        return this.http.get<any>(
            `${API_V3_URL}/status_pages/internal/${slug}/maintenance/upcoming`,
            { params, headers: this.getHeaders(token) }
        );
    }

    getInternalServiceUptimeBySlug(
        slug: string,
        serviceId: string | number,
        days?: number,
        token?: string
    ): Observable<any> {
        let params = new HttpParams();
        if (days !== undefined) params = params.set('days', String(days));
        return this.http.get<any>(
            `${API_V3_URL}/status_pages/internal/${slug}/services/${serviceId}/uptime`,
            { params, headers: this.getHeaders(token) }
        );
    }

    getInternalServiceHistoryBySlug(
        slug: string,
        serviceId: string | number,
        options?: {
            days?: number;
            pageSize?: number;
            after?: string;
            before?: string;
        },
        token?: string
    ): Observable<any> {
        let params = new HttpParams();
        if (options?.days !== undefined) params = params.set('days', String(options.days));
        if (options?.pageSize !== undefined) params = params.set('pageSize', String(options.pageSize));
        if (options?.after) params = params.set('after', options.after);
        if (options?.before) params = params.set('before', options.before);
        return this.http.get<any>(
            `${API_V3_URL}/status_pages/internal/${slug}/services/${serviceId}/history`,
            { params, headers: this.getHeaders(token) }
        );
    }

    subscribeInternalStatusPageBySlug(slug: string, payload: any, token?: string): Observable<any> {
        return this.http.post<any>(
            `${API_V3_URL}/status_pages/internal/${slug}/subscribe`,
            payload,
            { headers: this.getHeaders(token) }
        );
    }

    unsubscribeInternalStatusPageBySlug(
        slug: string,
        subscriberType?: string,
        token?: string
    ): Observable<any> {
        let params = new HttpParams();
        if (subscriberType) params = params.set('subscriberType', subscriberType);
        return this.http.post<any>(
            `${API_V3_URL}/status_pages/internal/${slug}/unsubscribe`,
            {},
            { params, headers: this.getHeaders(token) }
        );
    }

    // PUBLIC STATUS PAGE â€“ SLUG ONLY

    getInternalSubscriberManage(
        statusPageId: string | number,
        token?: string
    ): Observable<ManageSubscriptionResponse> {
        return this.http.get<ManageSubscriptionResponse>(
            `${API_V3_URL}/status_pages/${statusPageId}/subscribers/me`,
            { headers: this.getHeaders(token) }
        );
    }

    updateInternalSubscriberManage(
        statusPageId: string | number,
        payload: UpdateSubscriberServicesRequest,
        token?: string
    ): Observable<void> {
        return this.http.put<void>(
            `${API_V3_URL}/status_pages/${statusPageId}/subscribers/me`,
            payload,
            { headers: this.getHeaders(token) }
        );
    }

    getPublicServicesBySlug(
        slug: string,
        ssoTokenOrOptions?: string | {
            grouped?: boolean;
            pageSize?: number;
            after?: string;
            before?: string;
            includeCount?: boolean;
        },
        maybeSsoToken?: string
    ): Observable<any> {
        const options = typeof ssoTokenOrOptions === 'string' || ssoTokenOrOptions === undefined
            ? undefined
            : ssoTokenOrOptions;
        const ssoToken = typeof ssoTokenOrOptions === 'string' ? ssoTokenOrOptions : maybeSsoToken;
        let params = new HttpParams();
        if (options?.grouped !== undefined) params = params.set('grouped', String(options.grouped));
        if (options?.pageSize !== undefined) params = params.set('pageSize', String(options.pageSize));
        if (options?.after) params = params.set('after', options.after);
        if (options?.before) params = params.set('before', options.before);
        if (options?.includeCount !== undefined) params = params.set('includeCount', String(options.includeCount));
        return this.http.get<any>(
            `${API_V3_URL}/public/status_pages/${slug}/services`,
            {
                params,
                headers: this.getPublicHeadersWithOptionalSsoToken(ssoToken)
            }
        );
    }

    getPublicIncidentsBySlug(
        slug: string,
        options?: PublicIncidentQueryOptions,
        ssoToken?: string
    ): Observable<any> {
        let params = new HttpParams();
        if (options?.startDate) params = params.set('startDate', options.startDate);
        if (options?.endDate) params = params.set('endDate', options.endDate);
        return this.http.get<any>(
            `${API_V3_URL}/public/status_pages/${slug}/incidents`,
            {
                params,
                headers: this.getPublicHeadersWithOptionalSsoToken(ssoToken)
            }
        );
    }

    getPublicActiveIncidentsBySlug(slug: string, ssoToken?: string): Observable<any> {
        return this.http.get<any>(
            `${API_V3_URL}/public/status_pages/${slug}/incidents/active`,
            { headers: this.getPublicHeadersWithOptionalSsoToken(ssoToken) }
        );
    }

    getPublicMaintenancesBySlug(slug: string, ssoToken?: string): Observable<any> {
        return this.getPublicUpcomingMaintenanceBySlug(slug, ssoToken);
    }

    getPublicMaintenanceBySlug(slug: string, ssoToken?: string): Observable<any> {
        return this.http.get<any>(
            `${API_V3_URL}/public/status_pages/${slug}/maintenance`,
            { headers: this.getPublicHeadersWithOptionalSsoToken(ssoToken) }
        );
    }

    getPublicUpcomingMaintenanceBySlug(
        slug: string,
        ssoToken?: string,
        daysAhead?: number
    ): Observable<any> {
        let params = new HttpParams();
        if (daysAhead !== undefined) params = params.set('daysAhead', String(daysAhead));
        return this.http.get<any>(
            `${API_V3_URL}/public/status_pages/${slug}/maintenance/upcoming`,
            {
                params,
                headers: this.getPublicHeadersWithOptionalSsoToken(ssoToken)
            }
        );
    }

    subscribePublicStatusPageBySlug(
        slug: string,
        payload: any,
        ssoToken?: string
    ): Observable<any> {
        return this.http.post<any>(
            `${API_V3_URL}/public/status_pages/${slug}/subscribers`,
            payload,
            { headers: this.getPublicHeadersWithOptionalSsoToken(ssoToken) }
        );
    }

    unsubscribePublicStatusPageBySlug(
        slug: string,
        subscriberId: string | number,
        ssoToken?: string
    ): Observable<any> {
        return this.http.delete<any>(
            `${API_V3_URL}/public/status_pages/${slug}/subscribers/${subscriberId}`,
            { headers: this.getPublicHeadersWithOptionalSsoToken(ssoToken) }
        );
    }

    // PUBLIC STATUS PAGE â€“ ORG + SLUG

    getPublicServicesByOrgSlug(
        org: string,
        slug: string,
        ssoTokenOrOptions?: string | {
            grouped?: boolean;
            pageSize?: number;
            after?: string;
            before?: string;
            includeCount?: boolean;
        },
        maybeSsoToken?: string
    ): Observable<any> {
        const options = typeof ssoTokenOrOptions === 'string' || ssoTokenOrOptions === undefined
            ? undefined
            : ssoTokenOrOptions;
        const ssoToken = typeof ssoTokenOrOptions === 'string' ? ssoTokenOrOptions : maybeSsoToken;
        let params = new HttpParams();
        if (options?.grouped !== undefined) params = params.set('grouped', String(options.grouped));
        if (options?.pageSize !== undefined) params = params.set('pageSize', String(options.pageSize));
        if (options?.after) params = params.set('after', options.after);
        if (options?.before) params = params.set('before', options.before);
        if (options?.includeCount !== undefined) params = params.set('includeCount', String(options.includeCount));
        return this.http.get<any>(
            `${API_V3_URL}/public/status_pages/${org}/${slug}/services`,
            {
                params,
                headers: this.getPublicHeadersWithOptionalSsoToken(ssoToken)
            }
        );
    }

    getPublicIncidentsByOrgSlug(
        org: string,
        slug: string,
        options?: PublicIncidentQueryOptions,
        ssoToken?: string
    ): Observable<any> {
        let params = new HttpParams();
        if (options?.startDate) params = params.set('startDate', options.startDate);
        if (options?.endDate) params = params.set('endDate', options.endDate);
        return this.http.get<any>(
            `${API_V3_URL}/public/status_pages/${org}/${slug}/incidents`,
            {
                params,
                headers: this.getPublicHeadersWithOptionalSsoToken(ssoToken)
            }
        );
    }

    getPublicIncidentByOrgSlug(
        org: string,
        slug: string,
        incidentId: string | number,
        ssoToken?: string
    ): Observable<any> {
        return this.http.get<any>(
            `${API_V3_URL}/public/status_pages/${org}/${slug}/incidents/${incidentId}`,
            { headers: this.getPublicHeadersWithOptionalSsoToken(ssoToken) }
        );
    }


    getPublicActiveIncidentsByOrgSlug(
        org: string,
        slug: string,
        ssoToken?: string
    ): Observable<any> {
        return this.http.get<any>(
            `${API_V3_URL}/public/status_pages/${org}/${slug}/incidents/active`,
            { headers: this.getPublicHeadersWithOptionalSsoToken(ssoToken) }
        );
    }

    getPublicMaintenancesByOrgSlug(
        org: string,
        slug: string,
        ssoToken?: string
    ): Observable<any> {
        return this.getPublicUpcomingMaintenanceByOrgSlug(org, slug, ssoToken);
    }

    getPublicMaintenanceByOrgSlug(
        org: string,
        slug: string,
        ssoToken?: string
    ): Observable<any> {
        return this.http.get<any>(
            `${API_V3_URL}/public/status_pages/${org}/${slug}/maintenance`,
            { headers: this.getPublicHeadersWithOptionalSsoToken(ssoToken) }
        );
    }

    getPublicMaintenanceWindowByOrgSlug(
        org: string,
        slug: string,
        maintenanceId: string | number,
        ssoToken?: string
    ): Observable<any> {
        return this.http.get<any>(
            `${API_V3_URL}/public/status_pages/${org}/${slug}/maintenance/${maintenanceId}`,
            { headers: this.getPublicHeadersWithOptionalSsoToken(ssoToken) }
        );
    }

    getPublicUpcomingMaintenanceByOrgSlug(
        org: string,
        slug: string,
        ssoToken?: string,
        daysAhead?: number
    ): Observable<any> {
        let params = new HttpParams();
        if (daysAhead !== undefined) params = params.set('daysAhead', String(daysAhead));
        return this.http.get<any>(
            `${API_V3_URL}/public/status_pages/${org}/${slug}/maintenance/upcoming`,
            {
                params,
                headers: this.getPublicHeadersWithOptionalSsoToken(ssoToken)
            }
        );
    }

    subscribePublicStatusPageByOrgSlug(
        org: string,
        slug: string,
        payload: any,
        ssoToken?: string
    ): Observable<any> {
        return this.http.post<any>(
            `${API_V3_URL}/public/status_pages/${org}/${slug}/subscribers`,
            payload,
            { headers: this.getPublicHeadersWithOptionalSsoToken(ssoToken) }
        );
    }

    subscribePublicStatusPage(
        org: string,
        slug: string,
        payload: any,
        ssoToken?: string
    ): Observable<any> {
        return this.http.post<any>(
            `${API_V3_URL}/public/status_pages/${org}/${slug}/subscribe`,
            payload,
            { headers: this.getPublicHeadersWithOptionalSsoToken(ssoToken) }
        );
    }

    verifyPublicStatusPageSubscription(
        org: string,
        slug: string,
        token: string,
        ssoToken?: string
    ): Observable<any> {
        const params = new HttpParams().set('token', token);
        return this.http.get<any>(
            `${API_V3_URL}/public/status_pages/${org}/${slug}/subscribe/verify`,
            {
                params,
                headers: this.getPublicHeadersWithOptionalSsoToken(ssoToken)
            }
        );
    }

    unsubscribePublicStatusPage(
        org: string,
        slug: string,
        token: string,
        ssoToken?: string
    ): Observable<any> {
        const params = new HttpParams().set('token', token);
        return this.http.post<any>(
            `${API_V3_URL}/public/status_pages/${org}/${slug}/unsubscribe`,
            { token },
            {
                params,
                headers: this.getPublicHeadersWithOptionalSsoToken(ssoToken)
            }
        );
    }

    getPublicSubscriberManageByToken(
        token: string,
        ssoToken?: string
    ): Observable<ManageSubscriptionResponse> {
        const params = new HttpParams().set('token', token);
        return this.http.get<ManageSubscriptionResponse>(
            `${API_V3_URL}/public/subscribers/manage`,
            {
                params,
                headers: this.getPublicHeadersWithOptionalSsoToken(ssoToken)
            }
        );
    }

    getPublicSubscriberManage(
        org: string,
        slug: string,
        token: string,
        ssoToken?: string
    ): Observable<ManageSubscriptionResponse> {
        const params = new HttpParams().set('token', token);
        return this.http.get<ManageSubscriptionResponse>(
            `${API_V3_URL}/public/status_pages/${org}/${slug}/subscribers/manage`,
            {
                params,
                headers: this.getPublicHeadersWithOptionalSsoToken(ssoToken)
            }
        );
    }

    updatePublicSubscriberManage(
        org: string,
        slug: string,
        token: string,
        payload: UpdateSubscriberServicesRequest,
        ssoToken?: string
    ): Observable<void> {
        const params = new HttpParams().set('token', token);
        return this.http.put<void>(
            `${API_V3_URL}/public/status_pages/${org}/${slug}/subscribers/manage`,
            payload,
            {
                params,
                headers: this.getPublicHeadersWithOptionalSsoToken(ssoToken)
            }
        );
    }

    unsubscribePublicSubscriberByToken(
        token: string,
        ssoToken?: string
    ): Observable<HttpResponse<void>> {
        const params = new HttpParams().set('token', token);
        return this.http.post<void>(
            `${API_V3_URL}/public/subscribers/unsubscribe`,
            {},
            {
                params,
                headers: this.getPublicHeadersWithOptionalSsoToken(ssoToken),
                observe: 'response'
            }
        );
    }

    unsubscribePublicStatusPageByOrgSlug(
        org: string,
        slug: string,
        subscriberId: string | number,
        ssoToken?: string
    ): Observable<any> {
        return this.http.delete<any>(
            `${API_V3_URL}/public/status_pages/${org}/${slug}/subscribers/${subscriberId}`,
            { headers: this.getPublicHeadersWithOptionalSsoToken(ssoToken) }
        );
    }

    getPublicIncidentRedirect(
        statusId: string | number,
        incidentId: string | number,
        ssoToken?: string
    ): Observable<any> {
        return this.http.get<any>(
            `${API_V3_URL}/public/redirect/incident/${statusId}/${incidentId}`,
            { headers: this.getPublicHeadersWithOptionalSsoToken(ssoToken) }
        );
    }

    getPublicMaintenanceRedirect(
        statusId: string | number,
        maintenanceId: string | number,
        ssoToken?: string
    ): Observable<any> {
        return this.http.get<any>(
            `${API_V3_URL}/public/redirect/maintenance/${statusId}/${maintenanceId}`,
            { headers: this.getPublicHeadersWithOptionalSsoToken(ssoToken) }
        );
    }

    addGroupedServices(
        token: string,
        statusPageId: number | string,
        payload: any
    ): Observable<any> {
        return this.http.post<any>(
            `${API_V3_URL}/status_pages/${statusPageId}/services/grouped`,
            payload,
            {
                headers: this.getHeaders(token)
            }
        );
    }

    updateGroupedServices(
        token: string,
        statusPageId: number | string,
        payload: any
    ): Observable<any> {
        return this.http.put<any>(
            `${API_V3_URL}/status_pages/${statusPageId}/services/grouped`,
            payload,
            {
                headers: this.getHeaders(token)
            }
        );
    }

    getGroupedServicesForStatusPage(
        token: string,
        statusPageId: number | string
    ): Observable<any> {
        return this.http.get<any>(
            `${API_V3_URL}/status_pages/${statusPageId}/services/grouped`,
            {
                headers: this.getHeaders(token)
            }
        );
    }

    // === Status Page Assets: Logo ===
    uploadStatusPageLogo(token: string, statusPageId: string | number, file: File): Observable<any> {
        const formData = new FormData();
        formData.append('file', file);
        return this.http.post<any>(`${API_V3_URL}/status_pages/${statusPageId}/assets/logo`, formData, {
            headers: this.getUploadHeaders(token)
        });
    }

    // === Status Page Assets: Favicon ===
    uploadStatusPageFavicon(token: string, statusPageId: string | number, file: File): Observable<any> {
        const formData = new FormData();
        formData.append('file', file);
        return this.http.post<any>(`${API_V3_URL}/status_pages/${statusPageId}/assets/favicon`, formData, {
            headers: this.getUploadHeaders(token)
        });
    }

    // === Status Page Assets: Delete ===
    deleteStatusPageAsset(token: string, statusPageId: string | number, assetType: string): Observable<any> {
        return this.http.delete<any>(`${API_V3_URL}/status_pages/${statusPageId}/assets/${assetType}`, {
            headers: this.getHeaders(token)
        });
    }

    // === Status Page SSO: Login ===
    getStatusPageSsoLogin(
        statusPageId: string | number,
        returnUrl: string,
        options?: { forceAuthn?: boolean }
    ): Observable<any> {
        let params = new HttpParams();
        if (returnUrl) params = params.set('returnUrl', returnUrl);
        if (options?.forceAuthn) {
            params = params.set('forceAuthn', 'true');
            params = params.set('prompt', 'login');
            params = params.set('max_age', '0');
        }
        return this.http.get<any>(`${API_V3_URL}/sso/status_pages/${statusPageId}/login`, {
            headers: this.getPublicSsoHeaders(),
            params,
            withCredentials: true
        });
    }

    getStatusPageSsoLoginUrl(
        statusPageId: string | number,
        returnUrl: string,
        options?: { forceAuthn?: boolean }
    ): string {
        const query = new URLSearchParams();
        if (returnUrl) query.set('returnUrl', returnUrl);
        if (options?.forceAuthn) {
            query.set('forceAuthn', 'true');
            query.set('prompt', 'login');
            query.set('max_age', '0');
        }
        const qs = query.toString();
        const queryString = qs ? `?${qs}` : '';
        return `${API_V3_URL}/sso/status_pages/${statusPageId}/login${queryString}`;
    }

    postStatusPageSsoCallback(statusPageId: string | number, payload: any): Observable<any> {
        return this.http.post<any>(`${API_V3_URL}/sso/status_pages/${statusPageId}/callback`, payload, {
            headers: this.getPublicSsoHeaders(),
            withCredentials: true
        });
    }

    // === Status Page SSO: Logout ===
    getStatusPageSsoLogoutUrl(statusPageId: string | number, returnUrl?: string): string {
        const query = new URLSearchParams();
        if (returnUrl) query.set('returnUrl', returnUrl);
        const qs = query.toString();
        const queryString = qs ? `?${qs}` : '';
        return `${API_V3_URL}/sso/status_pages/${statusPageId}/logout${queryString}`;
    }

    // === Status Page SSO: Session ===
    getStatusPageSsoSession(statusPageId: string | number, ssoToken?: string): Observable<any> {
        return this.http.get<any>(`${API_V3_URL}/sso/status_pages/${statusPageId}/session`, {
            headers: this.getPublicSsoHeaders(ssoToken),
            withCredentials: true
        });
    }

    // === Status Page SSO: Test ===
    getStatusPageSsoTest(statusPageId: string | number): Observable<any> {
        return this.http.get<any>(`${API_V3_URL}/sso/status_pages/${statusPageId}/test`, {
            headers: this.getPublicSsoHeaders(),
            withCredentials: true
        });
    }

    // === Status Page SSO: Test Result ===
    getStatusPageSsoTestResult(
        statusPageId: string | number,
        params: { testId?: string; sso_token?: string; error?: string }
    ): Observable<any> {
        let queryParams = new HttpParams();
        if (params.testId) queryParams = queryParams.set('testId', params.testId);
        if (params.sso_token) queryParams = queryParams.set('sso_token', params.sso_token);
        if (params.error) queryParams = queryParams.set('error', params.error);

        return this.http.get<any>(`${API_V3_URL}/sso/status_pages/${statusPageId}/test/result`, {
            headers: this.getPublicSsoHeaders(),
            params: queryParams,
            withCredentials: true
        });
    }

    // === Public Status Page Details ===
    getPublicStatusPageById(statusPageId: string | number, ssoToken?: string): Observable<any> {
        return this.http.get<any>(`${API_V3_URL}/public/status_pages/${statusPageId}`, {
            headers: this.getPublicHeadersWithOptionalSsoToken(ssoToken)
        });
    }

    getPublicStatusPageByOrgSlug(org: string, slug: string, ssoToken?: string): Observable<any> {
        return this.http.get<any>(`${API_V3_URL}/public/status_pages/${org}/${slug}`, {
            headers: this.getPublicHeadersWithOptionalSsoToken(ssoToken)
        });
    }

    getPublicStatusPageByAlias(alias: string, ssoToken?: string): Observable<any> {
        return this.http.get<any>(`${API_V3_URL}/public/status_pages/by_alias/${encodeURIComponent(alias)}`, {
            headers: this.getPublicHeadersWithOptionalSsoToken(ssoToken)
        });
    }
    /**
     * Map API service data to the frontend ServiceItem model.
     * Centralizing this here ensures consistency across standalone and grouped lists.
     */
    public mapToServiceItem(data: any): ServiceItem {
        return {
            serviceId: data.serviceId || data.id,
            name: data.name,
            description: data.description || '',
            currentStatus: data.currentStatus || data.status,
            currentStatusName: data.currentStatusName || '',
            serviceGroupId: data.serviceGroupId || null,
            serviceGroupName: data.serviceGroupName || '',
            lastStatusChangeDate: data.lastStatusChangeDate || new Date().toISOString(),
            minutesInCurrentStatus: data.minutesInCurrentStatus || 0,
            statusPageCount: data.statusPageCount || 0,
            displayOrder: data.displayOrder || 0,
            hasAccess: data?.hasAccess !== false
        };
    }
}


